import { useState, useEffect } from 'react'
import { Dialog, DialogContent, Box, Typography, IconButton, Divider, TextField } from '@mui/material'
import { Close, Google } from '@mui/icons-material'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../../context/AuthContext'
import { designSpecs } from '../../theme/designSpecs'
import Button from '../common/Button'
import PasswordStep from './PasswordStep'

const AuthModal = ({ open, onClose, defaultMode = 'signup' }) => {
  const navigate = useNavigate()
  const { login, signup, loginWithGoogle } = useAuth()
  const [step, setStep] = useState('initial') // 'initial' | 'password'
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [emailError, setEmailError] = useState('')
  const [authError, setAuthError] = useState('')
  const [isSignUp, setIsSignUp] = useState(defaultMode === 'signup')
  const [googleLoading, setGoogleLoading] = useState(false)

  // Reset auth mode when modal opens or defaultMode changes
  useEffect(() => {
    if (open) {
      setIsSignUp(defaultMode === 'signup')
      setGoogleLoading(false)
    }
  }, [open, defaultMode])

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    return emailRegex.test(email)
  }

  const handleGoogleLogin = async () => {
    setGoogleLoading(true)
    setAuthError('')

    const result = await loginWithGoogle()

    if (!result.success) {
      setAuthError(result.error)
      setGoogleLoading(false)
    }
    // If successful, user will be redirected to Google
    // Don't set loading to false here - let the redirect happen
  }

  const handleEmailContinue = () => {
    if (!email.trim()) {
      setEmailError('Please enter your email')
      return
    }

    if (!validateEmail(email)) {
      setEmailError('Please enter a valid email address')
      return
    }

    setEmailError('')
    setStep('password')
  }

  const handlePasswordSubmit = async (submittedPassword, signUp) => {
    setPassword(submittedPassword)
    setIsSignUp(signUp)
    setAuthError('')

    // Real Stytch login/signup
    const result = signUp
      ? await signup(email, submittedPassword)
      : await login(email, submittedPassword)

    if (result.success) {
      onClose()
      navigate('/dashboard')
    } else {
      // Set error message from API
      setAuthError(result.error)
    }
  }

  const handleBack = () => {
    setStep('initial')
    setPassword('')
    setAuthError('')
  }

  const handleModalClose = () => {
    setStep('initial')
    setEmail('')
    setPassword('')
    setEmailError('')
    setAuthError('')
    setIsSignUp(defaultMode === 'signup')
    onClose()
  }

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && step === 'initial') {
      handleEmailContinue()
    }
  }

  return (
    <Dialog
      open={open}
      onClose={handleModalClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: designSpecs.borderRadius['2xl'],
          p: 2,
          maxWidth: 480,
        },
      }}
    >
      <Box sx={{ position: 'relative' }}>
        <IconButton
          onClick={handleModalClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: 'grey.500',
            '&:hover': {
              bgcolor: 'grey.100',
            },
          }}
        >
          <Close />
        </IconButton>

        <DialogContent sx={{ px: 4, py: 3 }}>
          {step === 'initial' && (
            <Box>
              <Typography
                variant="h3"
                sx={{
                  textAlign: 'center',
                  mb: 1,
                  fontWeight: 700,
                  color: 'grey.900',
                }}
              >
                Welcome to Mocksy
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  textAlign: 'center',
                  mb: 4,
                  color: 'grey.600',
                }}
              >
                Sign in or create an account to continue
              </Typography>

              {/* Google Sign In */}
              <Button
                fullWidth
                variant="outlined"
                size="large"
                onClick={handleGoogleLogin}
                disabled={googleLoading}
                startIcon={googleLoading ? null : <Google />}
                sx={{
                  mb: 2,
                  py: 1.5,
                  borderColor: 'grey.300',
                  color: 'grey.900',
                  fontWeight: 600,
                  '&:hover': {
                    borderColor: 'grey.400',
                    bgcolor: 'grey.50',
                  },
                  '&.Mui-disabled': {
                    borderColor: 'grey.200',
                    color: 'grey.500',
                  },
                }}
              >
                {googleLoading ? 'Redirecting to Google...' : 'Continue with Google'}
              </Button>

              <Divider sx={{ my: 3 }}>
                <Typography variant="body2" sx={{ color: 'grey.500', px: 2 }}>
                  or
                </Typography>
              </Divider>

              {/* Email TextField */}
              <TextField
                fullWidth
                type="email"
                placeholder="name@company.com"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value)
                  setEmailError('')
                }}
                onKeyPress={handleKeyPress}
                error={!!emailError}
                helperText={emailError}
                sx={{
                  mb: 2,
                  '& .MuiOutlinedInput-root': {
                    fontSize: designSpecs.typography.sizes.base,
                    borderRadius: designSpecs.borderRadius.lg,
                    '& fieldset': {
                      borderWidth: 2,
                      borderColor: emailError ? 'error.main' : 'grey.300',
                    },
                    '&:hover fieldset': {
                      borderColor: emailError ? 'error.main' : 'grey.400',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: emailError ? 'error.main' : 'primary.main',
                      borderWidth: 2,
                    },
                  },
                }}
              />

              {/* Continue with Email Button */}
              <Button
                fullWidth
                variant="outlined"
                size="large"
                onClick={handleEmailContinue}
                sx={{
                  py: 1.5,
                  borderColor: 'grey.300',
                  color: 'grey.900',
                  fontWeight: 600,
                  '&:hover': {
                    borderColor: 'grey.400',
                    bgcolor: 'grey.50',
                  },
                }}
              >
                Continue with Email
              </Button>
            </Box>
          )}

          {step === 'password' && (
            <PasswordStep
              email={email}
              onSubmit={handlePasswordSubmit}
              onBack={handleBack}
              defaultMode={isSignUp ? 'signup' : 'login'}
              error={authError}
            />
          )}
        </DialogContent>
      </Box>
    </Dialog>
  )
}

export default AuthModal
